/**
 * Contacts API Service
 * Universal search for contacts across all organizations
 */

import { api } from './client';

export interface ContactSearchResult {
  personId: number | null; // Null for org email results
  firstName: string | null; // Null for org email results
  lastName: string | null; // Null for org email results
  preferredName: string | null;
  displayName: string; // For org emails: email type name (e.g., "Human Resources")
  email: string | null;
  phone: string | null;
  positionTitle: string | null;
  roleCategory: string; // "Organization Email" for org email results
  organizationId: number;
  organizationName: string;
  organizationType: string | null;
  leaId: number | null;
  leaType: string | null;
  parentLeaId: number | null;
  parentLeaName: string | null;
  includeInDirectory: boolean;
  standardizedRoles?: string[]; // SEOAA role descriptors
  resultType?: 'person' | 'organizationEmail'; // Default: 'person'
}

export interface PaginatedContactSearchResponse {
  results: ContactSearchResult[];
  total: number;
  skip: number;
  limit: number;
}

export interface ContactSearchParams {
  search?: string;
  organizationId?: number;
  roleType?: string;
  roleTag?: string; // SEOAA role descriptor filter (comma-separated for multiple)
  includeAdministrators?: boolean;
  includeBoard?: boolean;
  includeSolicitors?: boolean;
  includePrincipals?: boolean;
  includeInDirectoryOnly?: boolean;
  includeInactive?: boolean; // Include ended/inactive assignments (superAdmin only)
  skip?: number;
  limit?: number;
}

export interface RoleTagOption {
  codeValue: string;
  shortDescription: string;
}

export const contactsApi = {
  /**
   * Get available role tags (standardized SEOAA roles)
   * Returns objects with codeValue (for filtering) and shortDescription (for display)
   */
  getRoleTags: async (): Promise<RoleTagOption[]> => {
    return api.get<RoleTagOption[]>('/contacts/role-tags');
  },

  /**
   * Search for contacts across all organizations
   * Returns paginated results with total count for server-side pagination
   */
  search: async (params: ContactSearchParams = {}): Promise<PaginatedContactSearchResponse> => {
    const queryParams = new URLSearchParams();

    if (params.search) queryParams.set('search', params.search);
    if (params.organizationId) queryParams.set('organizationId', params.organizationId.toString());
    if (params.roleType) queryParams.set('roleType', params.roleType);
    if (params.roleTag) queryParams.set('roleTag', params.roleTag);
    if (params.includeAdministrators !== undefined) queryParams.set('includeAdministrators', params.includeAdministrators.toString());
    if (params.includeBoard !== undefined) queryParams.set('includeBoard', params.includeBoard.toString());
    if (params.includeSolicitors !== undefined) queryParams.set('includeSolicitors', params.includeSolicitors.toString());
    if (params.includePrincipals !== undefined) queryParams.set('includePrincipals', params.includePrincipals.toString());
    if (params.includeInDirectoryOnly !== undefined) queryParams.set('includeInDirectoryOnly', params.includeInDirectoryOnly.toString());
    if (params.includeInactive !== undefined) queryParams.set('includeInactive', params.includeInactive.toString());
    if (params.skip) queryParams.set('skip', params.skip.toString());
    if (params.limit) queryParams.set('limit', params.limit.toString());

    const query = queryParams.toString();
    return api.get<PaginatedContactSearchResponse>(`/contacts${query ? `?${query}` : ''}`);
  },

  /**
   * Export contacts to XLSX, CSV, or JSON
   * @param includeAddresses - Include address columns in export (SuperAdmin only)
   */
  export: async (params: ContactSearchParams & {
    format?: 'xlsx' | 'csv' | 'json';
    includeAddresses?: boolean;  // SuperAdmin only - include home address columns
  } = {}): Promise<Blob> => {
    const queryParams = new URLSearchParams();

    queryParams.set('format', params.format || 'xlsx');
    if (params.search) queryParams.set('search', params.search);
    if (params.organizationId) queryParams.set('organizationId', params.organizationId.toString());
    if (params.roleType) queryParams.set('roleType', params.roleType);
    if (params.roleTag) queryParams.set('roleTag', params.roleTag);
    if (params.includeAdministrators !== undefined) queryParams.set('includeAdministrators', params.includeAdministrators.toString());
    if (params.includeBoard !== undefined) queryParams.set('includeBoard', params.includeBoard.toString());
    if (params.includeSolicitors !== undefined) queryParams.set('includeSolicitors', params.includeSolicitors.toString());
    if (params.includePrincipals !== undefined) queryParams.set('includePrincipals', params.includePrincipals.toString());
    if (params.includeInDirectoryOnly !== undefined) queryParams.set('includeInDirectoryOnly', params.includeInDirectoryOnly.toString());
    if (params.includeInactive !== undefined) queryParams.set('includeInactive', params.includeInactive.toString());
    if (params.includeAddresses) queryParams.set('includeAddresses', 'true');

    return api.getBlob(`/contacts/export?${queryParams.toString()}`);
  }
};